/**
 * Central UTM helper for outbound link tracking.
 *
 * Canonical standard:
 *   utm_source  = farmly
 *   utm_medium  = referral
 *   utm_campaign = context-specific (producer_page, place_page, etc.)
 *   utm_content  = entity slug/id (optional)
 *
 * Rules:
 *   - Only applies to external http(s) URLs
 *   - Preserves existing query params
 *   - Never overwrites UTM params already present on the URL
 *   - Returns the original string unchanged for invalid/empty/internal URLs
 */

export interface UtmParams {
  source?: string;
  medium?: string;
  campaign: string;
  content?: string;
}

const DEFAULTS = {
  source: 'farmly',
  medium: 'referral',
} as const;

/**
 * Append UTM query parameters to an external URL.
 * Safe to call on any string — returns the input unchanged if it's not
 * a valid external URL or already contains UTM params.
 */
export function appendUtmParams(url: string | null | undefined, params: UtmParams): string {
  if (!url) return '';

  // Only tag external http(s) URLs
  if (!/^https?:\/\//i.test(url)) return url;

  try {
    const parsed = new URL(url);

    // Don't overwrite existing UTM params
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;
    const hasExistingUtm = utmKeys.some((k) => parsed.searchParams.has(k));
    if (hasExistingUtm) return url;

    parsed.searchParams.set('utm_source', params.source ?? DEFAULTS.source);
    parsed.searchParams.set('utm_medium', params.medium ?? DEFAULTS.medium);
    parsed.searchParams.set('utm_campaign', params.campaign);
    if (params.content) {
      parsed.searchParams.set('utm_content', params.content);
    }

    return parsed.toString();
  } catch {
    // Malformed URL — return as-is
    return url;
  }
}
